import { ApiError } from "api/client";
import { addEntityTreeLink, createEntity, getTreeEntities, removeEntityTreeLink, updateEntity } from "api/entities";
import { addRelation, getEntityRelations, removeRelation } from "api/relations";
import type { CustomPropertyDto, EntityDto, EntityRelationDto, LinkedTreeDto, ReferenceLinkDto } from "api/types";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { flipRelationPerspective } from "utilities";

import CustomAttrRow from "./CustomAttrRow";
import { byEntityTitle, UNRESTRICTED_OPTIONS } from "./entityFormConfigs";
import type {
	CustomProp,
	EntityDialogProps,
	PlaceFormState,
	ReferenceLinkDraft,
	RelationDraft,
	RelationOption,
	TreeLinkDraft
} from "./entityForms.types";
import EntityImageGallery from "./EntityImageGallery";
import LineIcon from "./LineIcon";
import ReferenceLinkRow from "./ReferenceLinkRow";
import RelationChipItem from "./RelationChipItem";
import RelationTypePicker from "./RelationTypePicker";
import TreeLinkPicker from "./TreeLinkPicker";
import { useEntityImages } from "./useEntityImages";

import styles from "./formDialog.module.css";

const PLACE_TARGET = ["Place"];

// Every option reads "this place is <type> of/in the target", so the forward edge is always From = this place.
const CONNECTION_OPTIONS: RelationOption[] = [
	{ value: "LocatedIn", targetTypes: PLACE_TARGET },
	{ value: "HomeOf", targetTypes: ["Person"], canBeEx: true },
	{ value: "SeatOf", targetTypes: ["Organization"], canBeEx: true },
	{ value: "Contains", targetTypes: PLACE_TARGET },
	{ value: "NeighboringRegion", targetTypes: PLACE_TARGET },
	{ value: "PartOfWorld", targetTypes: PLACE_TARGET },
	// A place is a valid From side for both of these; only the Species and Organization forms
	// offered their halves, so a habitat or a guarded place could not be recorded from here.
	{ value: "HabitatOf", targetTypes: ["Species", "Creature"] },
	{ value: "ProtectedBy", targetTypes: ["Person", "Organization"], canBeEx: true },
	...UNRESTRICTED_OPTIONS,
	{ value: "Other", targetTypes: ["Person", "Place", "Object", "Creature", "Organization"], canBeEx: true }
];

const PLACE_TYPE_SUGGESTIONS = [
	"Castle",
	"City",
	"Town",
	"Village",
	"Fortress",
	"Kingdom",
	"Region",
	"Forest",
	"Mountain",
	"Island",
	"Continent",
	"World",
	"Landmark"
];

const newId = (): string =>
	typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const createInitialForm = (entity?: EntityDto | null): PlaceFormState => ({
	country: entity?.country ?? "",
	description: entity?.description ?? "",
	latitude: entity?.latitude != null ? String(entity.latitude) : "",
	longitude: entity?.longitude != null ? String(entity.longitude) : "",
	name: entity?.title ?? "",
	placeType: entity?.placeType ?? "",
	region: entity?.region ?? "",
	world: entity?.world ?? ""
});

/** Parses an optional coordinate field; returns undefined for blank or non-numeric input. */
const parseCoordinate = (value: string): number | undefined => {
	const trimmed = value.trim();
	if (!trimmed) {
		return undefined;
	}

	const parsed = Number(trimmed);

	return Number.isFinite(parsed) ? parsed : undefined;
};

const PlaceFormDialog = ({ entity, treeId, onClose, onCreated }: EntityDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const isEdit = Boolean(entity);

	const [form, setForm] = useState<PlaceFormState>(() => createInitialForm(entity));
	const gallery = useEntityImages({
		entityId: entity?.id ?? null,
		initialImages: entity?.images,
		getAccessToken
	});
	const [customProps, setCustomProps] = useState<CustomProp[]>(
		() =>
			entity?.customProperties?.map((prop: CustomPropertyDto) => ({
				id: prop.id,
				key: prop.key,
				value: prop.value ?? ""
			})) ?? []
	);
	const [referenceLinks, setReferenceLinks] = useState<ReferenceLinkDraft[]>(
		() => entity?.referenceLinks?.map((link: ReferenceLinkDto) => ({ id: link.id, url: link.url })) ?? []
	);
	const [treeLinks, setTreeLinks] = useState<TreeLinkDraft[]>(
		() =>
			entity?.linkedTrees?.map((link: LinkedTreeDto) => ({
				id: link.id,
				existingId: link.id,
				treeId: link.treeId,
				title: link.title
			})) ?? []
	);
	const [removedTreeLinkIds, setRemovedTreeLinkIds] = useState<string[]>([]);
	const [treeEntities, setTreeEntities] = useState<EntityDto[]>([]);
	const [connectionType, setConnectionType] = useState<string>("");
	const [connectionTarget, setConnectionTarget] = useState<string>("");
	const [connectionIsEx, setConnectionIsEx] = useState(false);
	const [connections, setConnections] = useState<RelationDraft[]>([]);
	const [removedConnectionIds, setRemovedConnectionIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");

	// Null until a type is picked, so an untouched dropdown can no longer record a relation
	// the author never chose. Everything downstream must cope with "nothing selected yet".
	const selectedOption = useMemo<RelationOption | null>(
		() => CONNECTION_OPTIONS.find((option: RelationOption) => option.value === connectionType) ?? null,
		[connectionType]
	);
	const availableTargets = useMemo<EntityDto[]>(
		() =>
			selectedOption
				? treeEntities
						.filter((item: EntityDto) => selectedOption.targetTypes.includes(item.type))
						.sort(byEntityTitle)
				: [],
		[selectedOption, treeEntities]
	);

	useEffect(() => {
		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getTreeEntities(treeId, token) : null))
			.then((list: EntityDto[] | null) => {
				if (active && list) {
					setTreeEntities(list.filter((item: EntityDto) => item.id !== entity?.id));
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity?.id, getAccessToken, treeId]);

	// Edit mode: show the place's existing connections so they can be removed.
	useEffect(() => {
		if (!entity) {
			return undefined;
		}

		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getEntityRelations(entity.id, token) : null))
			.then((list: EntityRelationDto[] | null) => {
				if (active && list) {
					setConnections(
						list.map((relation: EntityRelationDto) => ({
							id: relation.id,
							existingId: relation.id,
							relationType: flipRelationPerspective(relation.relationType),
							isEx: relation.isEx,
							targetId: relation.relatedEntityId,
							targetTitle: relation.relatedEntityTitle
						}))
					);
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity, getAccessToken]);

	const updateField = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
			const { name, value } = event.target;
			setForm((current: PlaceFormState) => ({ ...current, [name]: value }));
		},
		[]
	);

	const addCustomProp = useCallback((): void => {
		setCustomProps((current: CustomProp[]) => [...current, { id: newId(), key: "", value: "" }]);
	}, []);

	const updateCustomProp = useCallback((id: string, field: "key" | "value", value: string): void => {
		setCustomProps((current: CustomProp[]) =>
			current.map((prop: CustomProp) => (prop.id === id ? { ...prop, [field]: value } : prop))
		);
	}, []);

	const removeCustomProp = useCallback((id: string): void => {
		setCustomProps((current: CustomProp[]) => current.filter((prop: CustomProp) => prop.id !== id));
	}, []);

	const addReferenceLink = useCallback((): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) => [...current, { id: newId(), url: "" }]);
	}, []);

	const updateReferenceLink = useCallback((id: string, url: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.map((link: ReferenceLinkDraft) => (link.id === id ? { ...link, url } : link))
		);
	}, []);

	const removeReferenceLink = useCallback((id: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.filter((link: ReferenceLinkDraft) => link.id !== id)
		);
	}, []);

	const addTreeLink = useCallback((linkTreeId: string, title: string): void => {
		setTreeLinks((current: TreeLinkDraft[]) => [...current, { id: newId(), treeId: linkTreeId, title }]);
	}, []);

	const removeTreeLink = useCallback(
		(id: string): void => {
			const removed = treeLinks.find((link: TreeLinkDraft) => link.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedTreeLinkIds((ids: string[]) => [...ids, existingId]);
			}

			setTreeLinks((current: TreeLinkDraft[]) => current.filter((link: TreeLinkDraft) => link.id !== id));
		},
		[treeLinks]
	);

	const addRelationDraft = useCallback((): void => {
		if (!selectedOption || !connectionTarget) {
			return;
		}

		const target = availableTargets.find((item: EntityDto) => item.id === connectionTarget);
		if (!target) {
			return;
		}

		setConnections((current: RelationDraft[]) => [
			...current,
			{
				id: newId(),
				relationType: connectionType,
				isEx: Boolean(selectedOption.canBeEx) && connectionIsEx,
				targetId: target.id,
				targetTitle: target.title
			}
		]);
		setConnectionTarget("");
		setConnectionIsEx(false);
	}, [availableTargets, connectionIsEx, connectionTarget, connectionType, selectedOption]);

	const removeRelationDraft = useCallback(
		(id: string): void => {
			const removed = connections.find((connection: RelationDraft) => connection.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedConnectionIds((ids: string[]) => [...ids, existingId]);
			}

			setConnections((current: RelationDraft[]) =>
				current.filter((connection: RelationDraft) => connection.id !== id)
			);
		},
		[connections]
	);

	const onConnectionTypeChange = useCallback((nextType: string): void => {
		setConnectionType(nextType);
		// The new type may allow a different set of targets, and may not support the ex modifier.
		setConnectionTarget("");
		setConnectionIsEx(false);
	}, []);

	const onConnectionTargetChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setConnectionTarget(event.target.value);
	}, []);

	const onConnectionIsExChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setConnectionIsEx(event.target.checked);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const name = form.name.trim();
			if (!name) {
				setError(t("editor.placeForm.nameRequired"));

				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setError("");
			setIsSaving(true);

			try {
				const fields = {
					title: name,
					latitude: parseCoordinate(form.latitude),
					longitude: parseCoordinate(form.longitude),
					country: form.country.trim() || undefined,
					world: form.world.trim() || undefined,
					placeType: form.placeType.trim() || undefined,
					region: form.region.trim() || undefined,
					description: form.description.trim() || undefined,
					customProperties: customProps
						.filter((prop: CustomProp) => prop.key.trim())
						.map((prop: CustomProp) => ({
							key: prop.key.trim(),
							value: prop.value.trim() || undefined
						})),
					referenceLinks: referenceLinks.map((link: ReferenceLinkDraft) => link.url.trim()).filter(Boolean)
				};

				const saved = entity
					? await updateEntity(entity.id, { ...fields, images: gallery.toInputPayload() }, token)
					: await createEntity(treeId, { type: "Place", ...fields, images: gallery.toInputPayload() }, token);

				await Promise.all(removedConnectionIds.map((relationId: string) => removeRelation(relationId, token)));

				// Every connection reads "this place is <type> of/in the target", so this place is the From side.
				await Promise.all(
					connections
						.filter((connection: RelationDraft) => !connection.existingId)
						.map((connection: RelationDraft) =>
							addRelation(
								{
									fromEntityId: saved.id,
									toEntityId: connection.targetId,
									relationType: connection.relationType,
									isEx: connection.isEx
								},
								token
							)
						)
				);

				await Promise.all(removedTreeLinkIds.map((linkId: string) => removeEntityTreeLink(linkId, token)));
				await Promise.all(
					treeLinks
						.filter((link: TreeLinkDraft) => !link.existingId)
						.map((link: TreeLinkDraft) => addEntityTreeLink(saved.id, link.treeId, token))
				);

				onCreated(saved.id);
				onClose();
			} catch (submitError) {
				setError(submitError instanceof ApiError ? submitError.message : t("editor.placeForm.saveError"));
			} finally {
				setIsSaving(false);
			}
		},
		[
			connections,
			customProps,
			entity,
			form,
			gallery,
			getAccessToken,
			onClose,
			onCreated,
			referenceLinks,
			removedConnectionIds,
			removedTreeLinkIds,
			t,
			treeId,
			treeLinks
		]
	);

	const hasTargets = availableTargets.length > 0;

	return (
		<div className={styles.personPanelOverlay}>
			<form
				ref={dialogRef}
				aria-labelledby="place-form-title"
				aria-modal="true"
				className={styles.personFormPanel}
				onSubmit={onSubmit}
				role="dialog">
				<div className={styles.personFormHeader}>
					<div className={styles.personFormHeaderCopy}>
						<h1 id="place-form-title">
							{isEdit ? t("editor.placeForm.editTitle") : t("editor.placeForm.addTitle")}
						</h1>
						<p>{t("editor.placeForm.subtitle")}</p>
					</div>

					<button
						aria-label={t("editor.placeForm.close")}
						className={styles.personFormCloseButton}
						onClick={onClose}
						type="button">
						<LineIcon name="close" />
					</button>
				</div>

				<div className={styles.personFormBody}>
					<div className={styles.personFormProfile}>
						<EntityImageGallery gallery={gallery} title={form.name} editable />
					</div>

					<div className={styles.personFormFields}>
						<h2>{t("editor.placeForm.standardAttributes")}</h2>

						<div className={styles.personFormGrid}>
							<div className={styles.personFormField}>
								<label htmlFor="place-name">
									{t("editor.placeForm.title")} <span className={styles.requiredStar}>*</span>
								</label>
								<input
									id="place-name"
									maxLength={fieldLimits.entityTitle}
									name="name"
									onChange={updateField}
									type="text"
									value={form.name}
									required
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="place-latitude">{t("editor.placeForm.latitude")}</label>
								<input
									id="place-latitude"
									inputMode="decimal"
									maxLength={fieldLimits.customPropertyValue}
									name="latitude"
									onChange={updateField}
									type="text"
									value={form.latitude}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="place-longitude">{t("editor.placeForm.longitude")}</label>
								<input
									id="place-longitude"
									inputMode="decimal"
									maxLength={fieldLimits.customPropertyValue}
									name="longitude"
									onChange={updateField}
									type="text"
									value={form.longitude}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="place-country">{t("editor.placeForm.country")}</label>
								<input
									id="place-country"
									maxLength={fieldLimits.customPropertyValue}
									name="country"
									onChange={updateField}
									type="text"
									value={form.country}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="place-world">{t("editor.placeForm.world")}</label>
								<input
									id="place-world"
									maxLength={fieldLimits.customPropertyValue}
									name="world"
									onChange={updateField}
									type="text"
									value={form.world}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="place-type">{t("editor.placeForm.type")}</label>
								<input
									id="place-type"
									list="place-type-suggestions"
									maxLength={fieldLimits.customPropertyValue}
									name="placeType"
									onChange={updateField}
									type="text"
									value={form.placeType}
								/>
								<datalist id="place-type-suggestions">
									{PLACE_TYPE_SUGGESTIONS.map((suggestion: string) => (
										<option key={suggestion} label={suggestion} value={suggestion} />
									))}
								</datalist>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="place-region">{t("editor.placeForm.region")}</label>
								<input
									id="place-region"
									maxLength={fieldLimits.customPropertyValue}
									name="region"
									onChange={updateField}
									type="text"
									value={form.region}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="place-description">{t("editor.placeForm.description")}</label>
								<textarea
									id="place-description"
									maxLength={fieldLimits.customPropertyValue}
									name="description"
									onChange={updateField}
									rows={4}
									value={form.description}
								/>
							</div>
						</div>
					</div>

					<section className={styles.personFormSection}>
						<h2>{t("editor.placeForm.connections")}</h2>
						<div className={styles.relationAddRow}>
							<RelationTypePicker
								controlClassName={styles.relationSelect}
								disabled={isSaving}
								onChange={onConnectionTypeChange}
								options={CONNECTION_OPTIONS}
								value={connectionType}
							/>
							<select
								aria-label={t("editor.placeForm.connectionTarget")}
								className={styles.relationSelect}
								disabled={!hasTargets || isSaving}
								onChange={onConnectionTargetChange}
								value={connectionTarget}>
								<option value="">{t("editor.placeForm.connectionTargetPlaceholder")}</option>
								{availableTargets.map((target: EntityDto) => (
									<option key={target.id} value={target.id}>
										{target.type === "Place" ? target.title : `${target.title} (${target.type})`}
									</option>
								))}
							</select>

							{selectedOption?.canBeEx && (
								<label className={styles.relationExToggle} htmlFor="connection-is-ex">
									<input
										checked={connectionIsEx}
										disabled={isSaving}
										id="connection-is-ex"
										onChange={onConnectionIsExChange}
										type="checkbox"
									/>
									<span>{t("editor.placeForm.formerConnection")}</span>
								</label>
							)}
							<button
								className={styles.relationAddButton}
								disabled={!connectionType || !connectionTarget || isSaving}
								onClick={addRelationDraft}
								type="button">
								<LineIcon name="plus" />
								<span>{t("editor.placeForm.addConnection")}</span>
							</button>
						</div>

						{!selectedOption && (
							<p className={styles.relationHint}>{t("editor.relationPicker.chooseTypeHint")}</p>
						)}
						{selectedOption && !hasTargets && (
							<p className={styles.relationHint}>{t("editor.placeForm.noTargetsHint")}</p>
						)}

						{connections.length > 0 && (
							<div className={styles.relationChips}>
								{connections.map((connection: RelationDraft) => (
									<RelationChipItem
										key={connection.id}
										onRemove={removeRelationDraft}
										relation={connection}
										removeLabel={t("editor.placeForm.removeConnection")}
									/>
								))}
							</div>
						)}
					</section>

					<TreeLinkPicker
						currentTreeId={treeId}
						links={treeLinks}
						onAdd={addTreeLink}
						onRemove={removeTreeLink}
					/>

					<section className={styles.personFormSection}>
						<h2>{t("editor.placeForm.customAttributes")}</h2>
						{customProps.map((prop: CustomProp) => (
							<CustomAttrRow
								key={prop.id}
								idPrefix="place"
								keyLabel={t("editor.placeForm.key")}
								onChange={updateCustomProp}
								onRemove={removeCustomProp}
								prop={prop}
								removeLabel={t("editor.placeForm.removeCustomAttribute")}
								valueLabel={t("editor.placeForm.value")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addCustomProp} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.placeForm.addCustomAttribute")}</span>
						</button>
					</section>

					<section className={styles.personFormSection}>
						<h2>{t("editor.placeForm.references")}</h2>
						{referenceLinks.map((link: ReferenceLinkDraft) => (
							<ReferenceLinkRow
								key={link.id}
								link={link}
								onChange={updateReferenceLink}
								onRemove={removeReferenceLink}
								removeLabel={t("editor.placeForm.removeReference")}
								urlLabel={t("editor.placeForm.referenceUrl")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addReferenceLink} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.placeForm.addReference")}</span>
						</button>
					</section>

					{error && <div className={styles.personFormError}>{error}</div>}
				</div>

				<div className={styles.personFormActions}>
					<button
						className={styles.personFormCancelButton}
						disabled={isSaving}
						onClick={onClose}
						type="button">
						{t("editor.placeForm.cancel")}
					</button>
					<button className={styles.personFormSaveButton} disabled={isSaving} type="submit">
						{isSaving ? t("editor.placeForm.saving") : t("editor.placeForm.save")}
					</button>
				</div>
			</form>
		</div>
	);
};

export default PlaceFormDialog;
